import type { Story, StyleConfig, Upload } from "@/react/story/types";
import type { CSSProperties } from "react";

class TemplatingUtilsWrapper {

    style(config: StyleConfig | null | undefined, whitelist: string[] | null = null, blacklist: string[] | null = null): CSSProperties {
        if (!config) {
            return {};
        }

        const possibleStyle: CSSProperties = {
            // text
            color: config.color ?? undefined,
            fontSize: TemplatingUtils.percent(config.fontSize),
            textAlign: config.textAlign ?? undefined,
            fontFamily: config.fontFamily ?? undefined,
            // shape
            height: TemplatingUtils.percent(config.height),
            maxWidth: TemplatingUtils.percent(config.maxWidth),
            borderRadius: TemplatingUtils.rem(config.borderRadius),
            // background
            backgroundColor: config.backgroundColor ?? undefined,
            backgroundImage: TemplatingUtils.backgroundImage(config.backgroundImage),
            backgroundSize: "cover",
            backgroundPosition: "center",
            // flex
            flexBasis: TemplatingUtils.percent(config.flexBasis),
            justifyContent: config.justifyContent,
            alignItems: config.alignItems,
            // backdrop
            backdropFilter: TemplatingUtils.backdropFilter(config.backdropFilter),
            // animation
            transform: TemplatingUtils.transformRotate(config.angle),
            animationDelay: TemplatingUtils.duration(config.animationDelay),
            animationDuration: TemplatingUtils.duration(config.animationDuration),
        };

        const style: CSSProperties = {};
        const possibleEntries = Object.entries(possibleStyle) as [ keyof CSSProperties, CSSProperties[keyof CSSProperties] ][];
        possibleEntries.forEach(([ key, value ]) => {
            if (
                (!Array.isArray(whitelist) || whitelist.includes(key as string))
                && (!Array.isArray(blacklist) || !blacklist.includes(key as string))
                && value
            ) {
                (style as Record<string, unknown>)[key as string] = value;
            }
        });

        return style;
    }

    backgroundImage(upload: Upload | null | undefined): string | undefined {
        return upload ? `url(${upload.uri})` : undefined;
    }

    transformRotate(angle: number | string | null | undefined): string | undefined {
        return null !== angle && undefined !== angle ? `rotate(${angle}deg)` : undefined;
    }

    rem(value: number | string | null | undefined): string | undefined {
        return null !== value && undefined !== value ? `${value}rem` : undefined;
    }

    percent(value: number | string | null | undefined): string | undefined {
        return null !== value && undefined !== value ? `${value}%` : undefined;
    }

    backdropFilter(value: number | string | null | undefined): string | undefined {
        return null !== value && undefined !== value ? `blur(${value}px)` : undefined;
    }

    duration(value: number | string | null | undefined): string | undefined {
        return null !== value && undefined !== value ? `${value}ms` : undefined;
    }

    getFontFaces(story: Story): Map<string, string> {
        const fonts = new Map();
        story.fonts.forEach(font => {
            if (!font.font) {
                return;
            }

            const face = `
      @font-face {
        font-family: "${font.family}";
        font-style: normal;
        font-weight: ${font.weights.join(" ")};
        font-display: swap;
        src: url("${font.font.uri}") format("${font.format}");
      }
      `;

            fonts.set(font.family, face);
        });

        return fonts;
    }

}

const TemplatingUtils = new TemplatingUtilsWrapper();

export default TemplatingUtils;
